import type React from 'react';

import type { EventId } from '@vlabs/api/luna-api/model';
import type { WithDataTestid, WithClassNameProp } from '@vlabs/uikit/types';
import cn from 'classnames';

import { useEventQuery } from '@vlabs/monokit/entities/events/api';
import {
  AgeAttributeCard,
  GenderAttributeCard,
  UpperBodyAttributeCard,
  LowerBodyAttributeCard,
  BackpackAttributeCard,
  HeadwearAttributeCard,
  ShoesAttributeCard,
} from '@vlabs/monokit/shared/ui/atribute-cards';

import { useEventAttributesTranslation } from './i18n';
import st from './shared.module.sass';
import { buildTestid } from '../../../shared/lib/utils';

interface EventBodyAttributesProps extends WithDataTestid, WithClassNameProp {
  eventId: EventId;
}

const displayName = 'EventBodyAttributes';
const testid = buildTestid(displayName);

function EventBodyAttributes({
  eventId,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: EventBodyAttributesProps): React.JSX.Element {
  const { t } = useEventAttributesTranslation();
  const { data: event } = useEventQuery(eventId);

  if (!event) return <span className={st.NoData}>{t('no_data')}</span>;

  const raw = event as unknown as {
    body_basic_attributes?: { apparent_gender?: unknown; apparent_age?: unknown };
    upper_body?: { headwear?: { apparent_color?: unknown; state?: unknown } };
    lower_body?: { shoes?: unknown };
    accessories?: { backpack?: { state?: unknown } };
  };
  const gender = raw.body_basic_attributes?.apparent_gender;
  const age = raw.body_basic_attributes?.apparent_age;
  const upperBody = raw.upper_body;
  const lowerBody = raw.lower_body;
  const backpack = raw.accessories?.backpack.state;

  return (
    <div
      className={cn(st.Attributes, className)}
      {...testid(dataTestId)}
    >
      {!!age && (
        <AgeAttributeCard
          age={age as never}
          className={st.AttributeCard}
        />
      )}

      {!!gender && (
        <GenderAttributeCard
          className={st.AttributeCard}
          gender={gender as never}
        />
      )}

      {!!upperBody && (
        <UpperBodyAttributeCard
          className={st.AttributeCard}
          values={upperBody as never}
        />
      )}

      {!!lowerBody && (
        <LowerBodyAttributeCard
          className={st.AttributeCard}
          values={lowerBody as never}
        />
      )}

      {!!backpack && (
        <BackpackAttributeCard
          backpack={backpack as never}
          className={st.AttributeCard}
        />
      )}

      {!!upperBody && (
        <HeadwearAttributeCard
          className={st.AttributeCard}
          color={upperBody.headwear.apparent_color as never}
          headwear={upperBody.headwear.state as never}
        />
      )}

      {!!lowerBody && (
        <ShoesAttributeCard
          className={st.AttributeCard}
          shoes={lowerBody.shoes as never}
        />
      )}
    </div>
  );
}

EventBodyAttributes.displayName = displayName;

export { EventBodyAttributes };
